'use client'

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  ReactNode,
} from 'react'
import posthog from 'posthog-js'
import {
  trackConsultationFormStarted,
  trackConsultationFormStepCompleted,
  trackConsultationFormSubmitted,
  trackConsultationFormAbandoned,
  trackLeadConversionPageview,
} from '../../lib/analytics'
import type { ConsultationFlowVariant } from '../../lib/consultationExperiment'
import { OFFER_PARAM } from '@/lib/eventOffers'
import type { AppliedOffer } from '../../components/EventOffers/OfferAppliedBanner'

// ============ FORM DATA INTERFACE ============
export interface ConsultationFormData {
  selectedPlan?: 'paid' | 'free'
  firstName?: string
  lastName?: string
  email?: string
  linkedinUrl?: string
  countryCode?: string
  phoneCountryIso?: string
  phone?: string
  helpType?: string
  personaType?: string
  message?: string
  referralSource?: string
  referralSourceOther?: string
  referringContactName?: string
  referringCompanyName?: string | null
  referringCompanyId?: number
  referringCompanySfId?: string | null
  referralType?: string
  campaignId?: string
  /** Slug of the event offer the visitor arrived with, from `?offer=`. */
  offerSlug?: string
}

// ============ STEP DEFINITIONS ============
export type StepId =
  | 'plan-selection'
  | 'contact-info'
  | 'how-can-we-help'
  | 'your-message'
  | 'referral-info'
  | 'review'
  | 'first-name'
  | 'last-name'
  | 'email'
  | 'phone'
  | 'help-type'
  | 'persona-type'
  | 'referral-source'
  | 'referral-other'
  | 'referral-details'

export interface Step {
  id: StepId
  title: string
  subtitle?: string
  stepNumber: number
}

export const CONTROL_STEPS: Step[] = [
  {
    id: 'plan-selection',
    title: 'Select Your Consultation Type',
    subtitle: 'Choose the consultation that fits your needs.',
    stepNumber: 1,
  },
  {
    id: 'contact-info',
    title: 'Contact Information',
    subtitle: "Let's start with your basic information",
    stepNumber: 2,
  },
  {
    id: 'how-can-we-help',
    title: 'How Can We Help You?',
    subtitle: 'Tell us about your immigration needs',
    stepNumber: 3,
  },
  {
    id: 'your-message',
    title: 'Tell Us More',
    subtitle: 'Share details about your situation',
    stepNumber: 4,
  },
  {
    id: 'referral-info',
    title: 'How Did You Hear About Us?',
    subtitle: 'Help us understand how you found us',
    stepNumber: 5,
  },
  {
    id: 'review',
    title: 'Review & Submit',
    subtitle: 'Please review your information before submitting',
    stepNumber: 6,
  },
]

export const TYPEFORM_STEPS: Step[] = [
  {
    id: 'first-name',
    title: "What's your first name?",
    stepNumber: 1,
  },
  {
    id: 'last-name',
    title: "And your last name?",
    stepNumber: 2,
  },
  {
    id: 'email',
    title: "What's your email address?",
    subtitle: "We'll use this to confirm your consultation.",
    stepNumber: 3,
  },
  {
    id: 'phone',
    title: "What's the best phone number to reach you?",
    stepNumber: 4,
  },
  {
    id: 'help-type',
    title: 'What type of immigration matter can we help with?',
    stepNumber: 5,
  },
  {
    id: 'persona-type',
    title: 'What best describes you?',
    stepNumber: 6,
  },
  {
    id: 'your-message',
    title: 'Tell us about your situation',
    subtitle: 'The more detail you share, the better we can prepare.',
    stepNumber: 7,
  },
  {
    id: 'plan-selection',
    title: 'Which consultation would work best for you?',
    subtitle: 'Now that we know a bit about your situation, choose how you’d like to connect.',
    stepNumber: 8,
  },
  {
    id: 'referral-source',
    title: 'How did you hear about us?',
    stepNumber: 9,
  },
  {
    id: 'referral-other',
    title: 'Please tell us how you found us',
    stepNumber: 10,
  },
  {
    id: 'referral-details',
    title: 'Who referred you?',
    subtitle: 'At least one field is required.',
    stepNumber: 11,
  },
  {
    id: 'review',
    title: 'Review & Submit',
    subtitle: 'Please review your information before submitting',
    stepNumber: 12,
  },
]

/** @deprecated Use CONTROL_STEPS */
export const STEPS = CONTROL_STEPS

export const TOTAL_STEPS = CONTROL_STEPS.length

function shouldSkipTypeformStep(stepId: StepId, formData: ConsultationFormData): boolean {
  if (stepId === 'referral-other') return formData.referralSource !== 'Unknown'
  if (stepId === 'referral-details') return formData.referralSource !== 'Referral'
  return false
}

/**
 * `loading` while an `?offer=` slug is being looked up, `live` once it
 * resolves, `none` when there is no slug or it didn't resolve to a live offer.
 */
export type OfferStatus = 'none' | 'loading' | 'live'

function getActiveSteps(
  flowVariant: ConsultationFlowVariant,
  formData: ConsultationFormData,
  offerStatus: OfferStatus,
) {
  const baseSteps = flowVariant === 'typeform' ? TYPEFORM_STEPS : CONTROL_STEPS
  // The offer defines the consultation, so there is no plan to choose. The step
  // is hidden while the lookup is in flight too, so it doesn't flash on screen
  // and then vanish; a slug that fails to resolve brings it back.
  const skipPlan = offerStatus !== 'none'
  return baseSteps.filter((step) => {
    if (skipPlan && step.id === 'plan-selection') return false
    return flowVariant !== 'typeform' || !shouldSkipTypeformStep(step.id, formData)
  })
}

function readOfferSlug(): string | undefined {
  if (typeof window === 'undefined') return undefined
  return new URLSearchParams(window.location.search).get(OFFER_PARAM)?.trim() || undefined
}

// ============ OPTIONS ============
export const HELP_TYPE_OPTIONS = [
  { value: 'Visa Application', label: 'Visa Application' },
  { value: 'Green Card / Permanent Residency', label: 'Green Card / Permanent Residency' },
  { value: 'Citizenship / Naturalization', label: 'Citizenship / Naturalization' },
  { value: 'Work Authorization', label: 'Work Authorization' },
  { value: 'Family Immigration', label: 'Family Immigration' },
  { value: 'Business / Investment Immigration', label: 'Business / Investment Immigration' },
  { value: 'Deportation Defense', label: 'Deportation Defense' },
  { value: 'Border Issue', label: 'Border Issue' },
  { value: 'Court Case', label: 'Court Case' },
  { value: 'Other', label: 'Other' },
]

export const PERSONA_OPTIONS = [
  { value: 'Employment (Individual)', label: 'Worker/Employee' },
  { value: 'Start-up', label: 'Entrepreneur' },
  { value: 'Investor', label: 'Investor' },
  { value: 'Student', label: 'Student' },
  { value: 'Family', label: 'Family' },
  { value: 'Employer', label: 'Employer' },
]

export const REFERRAL_OPTIONS = [
  { value: 'Internet Search', label: 'Internet Search' },
  { value: 'Website', label: 'Website' },
  { value: 'Social Media', label: 'Social Media' },
  { value: 'AI Chat', label: 'AI / Chatbot' },
  { value: 'Referral', label: 'I was referred by someone' },
  { value: 'Client', label: "I'm a previous client" },
  { value: 'Corporate Client', label: 'My employer is an existing client' },
  { value: 'Seminar', label: 'Seminar or Conference' },
  { value: 'Unknown', label: 'Other' },
]

// ============ CONTEXT INTERFACE ============
interface ConsultationContextType {
  flowVariant: ConsultationFlowVariant
  currentStepId: StepId
  currentStep: Step
  currentStepIndex: number
  direction: number
  goToNextStep: () => void
  goToPreviousStep: () => void
  goToStep: (stepId: StepId) => void
  canGoBack: boolean
  canGoForward: boolean
  formData: ConsultationFormData
  updateFormData: (data: Partial<ConsultationFormData>) => void
  progress: number
  totalSteps: number
  isSubmitting: boolean
  isSubmitted: boolean
  submitForm: () => Promise<void>
  /** The live offer applied from `?offer=`, once it resolves. */
  offer: AppliedOffer | null
  offerStatus: OfferStatus
}

const ConsultationContext = createContext<ConsultationContextType | undefined>(undefined)

// ============ PROVIDER ============
interface ConsultationProviderProps {
  children: ReactNode
  flowVariant: ConsultationFlowVariant
}

export function ConsultationProvider({ children, flowVariant }: ConsultationProviderProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  // Read up front rather than in an effect so the plan step is already hidden
  // on the first render. Carried with the lead so the offer the banner promises
  // is actually recorded against them, rather than only shown on screen.
  const [formData, setFormData] = useState<ConsultationFormData>(() => ({
    selectedPlan: 'free',
    countryCode: '+1',
    phoneCountryIso: 'US',
    offerSlug: readOfferSlug(),
  }))
  const [offer, setOffer] = useState<AppliedOffer | null>(null)
  const [offerStatus, setOfferStatus] = useState<OfferStatus>(() =>
    formData.offerSlug ? 'loading' : 'none',
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const startTimeRef = useRef(Date.now())
  const hasTrackedStart = useRef(false)
  const formDataRef = useRef(formData)
  formDataRef.current = formData
  const offerStatusRef = useRef(offerStatus)
  offerStatusRef.current = offerStatus

  const activeSteps = useMemo(
    () => getActiveSteps(flowVariant, formData, offerStatus),
    [flowVariant, formData, offerStatus],
  )

  const clampedIndex = Math.min(currentStepIndex, Math.max(activeSteps.length - 1, 0))
  const currentStep = activeSteps[clampedIndex] ?? activeSteps[0]
  const currentStepId = currentStep.id

  useEffect(() => {
    if (clampedIndex !== currentStepIndex) {
      setCurrentStepIndex(clampedIndex)
    }
  }, [clampedIndex, currentStepIndex])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const campaignId = params.get('campaignId')
    if (campaignId) {
      setFormData((prev) => ({ ...prev, campaignId }))
    }
  }, [])

  // Fetched rather than server-rendered so that `/consultation` stays static
  // for the majority of visitors, who arrive with no offer at all.
  useEffect(() => {
    const slug = formData.offerSlug
    if (!slug) return

    const controller = new AbortController()
    fetch(`/api/event-offer?${OFFER_PARAM}=${encodeURIComponent(slug)}`, {
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const resolved: AppliedOffer | null = data?.offer ?? null
        setOffer(resolved)
        setOfferStatus(resolved ? 'live' : 'none')
      })
      .catch((error) => {
        // An offline or failed lookup just means no offer; the form still works.
        if (error?.name !== 'AbortError') setOfferStatus('none')
      })

    return () => controller.abort()
  }, [formData.offerSlug])

  useEffect(() => {
    if (hasTrackedStart.current) return
    hasTrackedStart.current = true
    trackConsultationFormStarted({
      consultation_type: formData.selectedPlan,
      entry_page: document.referrer || '$direct',
      form_flow_variant: flowVariant,
    })
  }, [flowVariant, formData.selectedPlan])

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isSubmitted) {
        trackConsultationFormAbandoned({
          last_step_completed: clampedIndex + 1,
          consultation_type: formData.selectedPlan,
          time_spent_seconds: Math.round((Date.now() - startTimeRef.current) / 1000),
          form_flow_variant: flowVariant,
        })
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [clampedIndex, formData.selectedPlan, flowVariant, isSubmitted])

  const updateFormData = useCallback((data: Partial<ConsultationFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }))
  }, [])

  const goToNextStep = useCallback(() => {
    const steps = getActiveSteps(flowVariant, formDataRef.current, offerStatusRef.current)
    setCurrentStepIndex((prev) => {
      if (prev >= steps.length - 1) return prev

      trackConsultationFormStepCompleted({
        step_number: prev + 1,
        step_name: steps[prev].id,
        consultation_type: formDataRef.current.selectedPlan,
        form_flow_variant: flowVariant,
      })
      setDirection(1)
      return prev + 1
    })
  }, [flowVariant])

  const goToPreviousStep = useCallback(() => {
    if (clampedIndex <= 0) return
    setDirection(-1)
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0))
  }, [clampedIndex])

  const goToStep = useCallback(
    (stepId: StepId) => {
      const index = activeSteps.findIndex((s) => s.id === stepId)
      if (index !== -1) {
        setDirection(index > clampedIndex ? 1 : -1)
        setCurrentStepIndex(index)
      }
    },
    [activeSteps, clampedIndex],
  )

  const canGoBack = clampedIndex > 0
  const canGoForward = clampedIndex < activeSteps.length - 1
  const progress = ((clampedIndex + 1) / activeSteps.length) * 100

  const submitForm = useCallback(async () => {
    setIsSubmitting(true)
    try {
      const token = await new Promise<string>((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!, { action: 'submit_consultation' })
            .then(resolve)
            .catch(reject)
        })
      })

      const referralType =
        formData.referringCompanyName?.trim() || formData.referringContactName?.trim()
          ? 'Indirect Referral'
          : 'No Referral'

      const res = await fetch('/api/submit-consultation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, formData: { ...formData, referralType } }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Submission failed')
      }

      setIsSubmitted(true)

      if (formData.email) {
        posthog.identify(formData.email, {
          name: [formData.firstName, formData.lastName].filter(Boolean).join(' '),
          phone: formData.phone,
          source: 'consultation_form',
        })
      }

      trackConsultationFormSubmitted({
        consultation_type: formData.selectedPlan || 'free',
        persona_segment: formData.personaType,
        referral_source: formData.referralSource,
        form_flow_variant: flowVariant,
      })

      // Route the URL to a dedicated thank-you page so the completion can be
      // measured as a lead-conversion pageview (keeps the success UI in place).
      const thankYouPath = '/consultation/thank-you'
      window.history.replaceState(window.history.state, '', thankYouPath)
      trackLeadConversionPageview({ path: thankYouPath, title: 'Consultation — Thank You' })
    } catch (error) {
      console.error('Error submitting form:', error)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }, [flowVariant, formData])

  const value: ConsultationContextType = {
    flowVariant,
    currentStepId,
    currentStep,
    currentStepIndex: clampedIndex,
    direction,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    canGoBack,
    canGoForward,
    formData,
    updateFormData,
    progress,
    totalSteps: activeSteps.length,
    isSubmitting,
    isSubmitted,
    submitForm,
    offer,
    offerStatus,
  }

  return <ConsultationContext.Provider value={value}>{children}</ConsultationContext.Provider>
}

export function useConsultation() {
  const context = useContext(ConsultationContext)
  if (context === undefined) {
    throw new Error('useConsultation must be used within a ConsultationProvider')
  }
  return context
}
